/**
 * Traduce el payload de `activity_log.details` (columna JSONB) a pares
 * etiqueta/valor legibles.
 *
 * El backend guarda claves distintas según la acción y no guarda la unidad del
 * producto, así que aquí no se añaden sufijos (g/ud). Las ubicaciones se guardan
 * como UUID crudo: se resuelven contra el mapa `locations` (id → nombre) que
 * pasa la página.
 *
 * `locations` es `null` cuando no se pudo cargar (un STAFF sin permiso de
 * productos recibe 403 de `/v1/stock/locations`). En ese caso se OMITEN las
 * ubicaciones en vez de pintar un UUID o inventarse un nombre.
 */

// Los decimales llegan como string ("250.000"). Se recortan los ceros de cola.
function fmtNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("es-ES", { maximumFractionDigits: 3 });
}

// Importes de caja: llegan como string ("165.00") y se pintan con dos decimales y el
// símbolo, como en la pantalla de cajas. Devuelve null si no hay dato — `push` lo
// descarta, así que nunca se interpola dentro de un texto (eso pintaba «null → 300 €»).
function fmtEuros(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });
}

// Cuándo se firmó el arqueo que se está corrigiendo. Llega en ISO.
function fmtFecha(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString("es-ES");
}

// Coste de la entrada de mercancía: es por unidad (€/g o €/ud), no un importe total.
// El backend no guarda la unidad del producto, así que no se sufija.
function fmtUnitCost(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `${n.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}/ud.`;
}

// Etiquetas de los campos del socio, para no pintarle `credit_balance` a un humano.
const CLIENT_FIELDS = {
  email: "Email",
  first_name: "Nombre",
  last_name: "Apellidos",
  national_id: "DNI",
  birth_date: "Fecha de nacimiento",
  address: "Dirección",
  postal_code: "Código postal",
  city: "Ciudad",
  phone: "Teléfono",
  credit_balance: "Saldo",
  enrollment_date: "Fecha de alta",
  expiration_date: "Fecha de caducidad",
  guarantor_member: "Avalista",
  fee: "Cuota",
  max_monthly_consumption: "Consumo máx. mensual",
  notes: "Notas",
  cost_line: "Línea de coste",
  discount: "Descuento",
  consumption_ludenos: "Consumo lúdico",
  consumption_terap: "Consumo terapéutico",
  // Campos censurados: el backend registra QUE cambiaron, nunca su valor.
  password: "Contraseña",
  documents_path: "Documentos",
  dni_documents_path: "Documentación DNI",
  dni_front_path: "DNI (anverso)",
  dni_back_path: "DNI (reverso)",
};

const clientField = (key) => CLIENT_FIELDS[key] || key;

// Campos cuyo VALOR no se pinta nunca, venga como venga del backend.
//
// El backend ya los redacta (los manda en `redacted_fields`, sin valor). Esto es la segunda
// cerradura: esta página la lee el STAFF, pinta los pares en la fila de la tabla y los mete
// en el buscador — si el backend regresara, un camarero podría teclear un DNI y encontrar al
// socio. Y el log no tiene FK al socio: lo que se escriba aquí sobrevive a su borrado.
//
// `consumption_terap` / `consumption_ludenos` son dato de SALUD (RGPD art. 9).
const NEVER_SHOW_VALUE = new Set([
  "national_id", "birth_date",
  "address", "postal_code", "city", "phone",
  "consumption_terap", "consumption_ludenos",
  "password", "documents_path", "dni_documents_path", "dni_front_path", "dni_back_path",
]);

// Un valor vacío se lee mejor como "—" que como una cadena en blanco.
function fmtChangeValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (value === true) return "Sí";
  if (value === false) return "No";
  return String(value);
}

export function formatActivityDetails(action, details, locations, summary = "") {
  if (!details || typeof details !== "object" || Array.isArray(details)) return [];

  const out = [];
  // La regla de este fichero dejó de cumplirse sola en cuanto hubo diez casos: el
  // detalle repetía el método de pago, el email o el nombre que el resumen ya canta
  // (reporte de Yago en staging, 08-09-2026). Ahora se comprueba, y solo con textos
  // largos y no numéricos: «20» aparecería dentro de «2026» y de cualquier importe.
  const resumen = String(summary || "").toLowerCase();
  const yaLoDiceElResumen = (valor) => {
    const v = String(valor).trim();
    if (v.length < 5 || /^[\d.,\s€%+-]+$/.test(v)) return false;
    return resumen.includes(v.toLowerCase());
  };
  const push = (label, value) => {
    if (value === null || value === undefined || value === "") return;
    if (yaLoDiceElResumen(value)) return;
    out.push({ label, value: String(value) });
  };
  // Una ubicación presente en el payload pero ausente del mapa ya cargado es una
  // ubicación borrada después del movimiento — el log es histórico, la lista no.
  // `locations` es legítimamente null: el STAFF recibe 403 de /v1/stock/locations. Sin esta
  // guarda, la próxima llamada a locName sin comprobar antes deja la página en blanco justo
  // a los usuarios de menos privilegio.
  const locName = (id) => {
    if (!locations) return null;
    return locations[id] || "Ubicación eliminada";
  };
  const pushLoc = (label, id) => {
    if (!id || !locations) return;
    push(label, locName(id));
  };

  // Regla: NO repetir lo que el `summary` ya dice. El summary de una entrada ya trae la
  // cantidad y el producto; el de un ajuste, la variación; el de una venta, el importe,
  // el método de pago y el número de líneas. Repetirlo era justo lo que hacía que el
  // desplegable no aportara nada.
  const PAYMENT_LABELS = { cash: "Efectivo", card: "Tarjeta", transfer: "Transferencia", credit: "Saldo" };
  // Por qué una anulación en efectivo no movió la caja (decidido 08-09-2026).
  const CAJA_SIN_TOCAR = {
    turno_ya_cerrado: "No se tocó: la aportación era de un turno ya cerrado, y aquel arqueo ya está firmado",
    sin_caja_abierta: "No se tocó: no hay ninguna caja abierta",
  };

  switch (action) {
    case "saldo.aportacion":
      // «Aportación de 20,00 € (efectivo) a «Carlos»» ya lo dice todo menos en qué
      // queda el socio. El método se filtra solo si el resumen lo trae.
      push("Método", PAYMENT_LABELS[String(details.payment_method || "").toLowerCase()] || details.payment_method);
      push("Saldo tras la aportación", fmtNumber(details.new_balance));
      break;

    case "socio.fee_reversed":
      // Sin `case` caía al `default` y enseñaba «amount: 20.0 · transaction_id:
      // 94e30f1d-…»: el importe ya está en el resumen y ese UUID no lo usa ninguna
      // pantalla. Solo queda lo que no se puede deducir: qué pasó con la caja.
      push("Caja", CAJA_SIN_TOCAR[details.caja_sin_tocar]);
      break;

    case "caja.apertura":
      // Con cuánto abre ya va en el resumen. Lo que no se deduce es si ese número sale
      // del recuento del cajón o lo escribió alguien — que es la diferencia entre un
      // dato contado y un dato declarado.
      // El importe ya va en el resumen; y con desglose, el total contado ES ese importe
      // (el backend hace mandar al recuento). Lo único que no se deduce es de dónde
      // sale el número: contado o declarado.
      push("De dónde sale", details.desde_recuento ? "Recuento de monedas y billetes" : "Escrita a mano");
      push("Nota", details.nota);
      break;

    case "caja.apertura_editada":
      // El de→a y el movimiento del esperado ya van en el resumen; aquí queda el
      // porqué, que es lo que el formulario obliga a escribir desde el 23-09-2026.
      push("De dónde sale", details.desde_recuento ? "Recuento de monedas y billetes" : "Escrita a mano");
      push("Nota", details.nota);
      break;

    case "caja.cierre":
      // El resumen lleva contado / esperado / diferencia. Falta en qué queda el cajón.
      push("Apertura del turno", fmtEuros(details.apertura));
      push("Cambio que se deja", fmtEuros(details.cambio_dejado));
      push("Retirada", fmtEuros(details.retirada));
      push("Nota", details.nota);
      break;

    case "caja.cierre_editado":
      // El antes→después de cada importe movido YA va en el resumen, campo a campo.
      // Repetirlo aquí pintaba el mismo dinero dos veces en la misma fila — y con dos
      // formatos distintos, «135.00 €» arriba y «135,00 €» abajo. Queda lo que no se
      // puede deducir: cuándo se firmó el arqueo que se está reescribiendo, y por qué.
      push("Arqueo firmado el", fmtFecha(details.cerrada_el));
      push("Nota", details.nota);
      break;

    case "product.delete":
    case "product.retire":
      // El nombre ya va en el resumen; lo que importa es el stock que se llevó por
      // delante (antes salía como «stock_quantity: 100.000»).
      push("Stock que tenía", fmtNumber(details.stock_quantity));
      break;

    case "saldo.aportacion_anulada":
      // Importe, método y socio ya van en el summary — repetirlos era lo que hacía
      // el detalle ilegible (reporte de Yago en staging, 08-09). Mismo formato que
      // «Venta anulada», que es la operación hermana.
      if (details.new_balance != null) push("Saldo resultante", `${Number(details.new_balance).toFixed(2)} €`);
      // Y lo único que no se puede deducir de nada: si el efectivo volvió a la caja.
      // Solo vuelve cuando la aportación era del turno ABIERTO; si no, se corrige el
      // saldo y no se toca caja de nadie, y quien arquea tiene que saberlo o buscará
      // un descuadre que no existe. Los UUIDs (transacción, sesión de caja) se quedan
      // en `details` para auditoría, nunca en pantalla.
      push("Caja", CAJA_SIN_TOCAR[details.caja_sin_tocar]);
      break;

    case "stock.entry":
    case "stock.exit":
      // `quantity` ya está en el summary.
      pushLoc("Ubicación", details.location_id);
      // Solo la entrada de mercancía tiene coste (lo que se pagó por el género).
      push("Coste", fmtUnitCost(details.unit_cost));
      push("Saldo", fmtNumber(details.balance_after));
      push("Concepto", details.concept);
      break;

    case "stock.adjust":
      // `new_quantity` y `delta` ya están en el summary.
      pushLoc("Ubicación", details.location_id);
      push("Saldo", fmtNumber(details.balance_after));
      push("Concepto", details.concept);
      break;

    case "stock.set_total":
      // `new_total` y `delta` ya están en el summary: solo el concepto es nuevo.
      push("Concepto", details.concept);
      break;

    case "stock.reconcile_total":
      // `new_total` y `delta` ya están en el summary. Lo que falta es de qué cifra se
      // venía, que es lo que hace entendible por qué se retiró género del total.
      push("Total anterior", details.previous_total);
      break;

    case "stock.transfer":
      // `quantity` ya está en el summary; lo que falta es de dónde a dónde.
      if (locations && (details.from_location_id || details.to_location_id)) {
        const from = details.from_location_id ? locName(details.from_location_id) : "—";
        const to = details.to_location_id ? locName(details.to_location_id) : "—";
        push("Movimiento", `${from} → ${to}`);
      }
      break;

    case "sale.create":
      // QUÉ se vendió, por nombre. Antes esto se dejaba fuera a propósito —vive en la
      // transacción y a eso baja el drill-down—, pero eso hacía la venta INENCONTRABLE:
      // al filtrar por «CRUA» salían los traspasos (que sí llevan el nombre) y ninguna
      // venta (reporte de Kong, 19-ago, cuadrando stock). Importe, pago, carrito y nº
      // de líneas siguen en el summary; `member_user_id` sigue sin resolver a nombre.
      if (details.productos) push("Productos", details.productos);
      break;

    case "sale.reverse":
      // Importe, método y nº de líneas ya van en el summary; solo el saldo resultante.
      if (details.new_balance != null) push("Saldo resultante", `${Number(details.new_balance).toFixed(2)} €`);
      break;

    case "sale.reverse_units":
      // Producto, unidades e importe devuelto ya van en el summary. Los UUIDs
      // (venta, línea, socio) se quedan en details para auditoría, no en pantalla.
      if (details.new_balance != null) push("Saldo resultante", `${Number(details.new_balance).toFixed(2)} €`);
      if (details.line_removed) push("Línea", "eliminada por completo");
      if (details.transaction_reversed) push("Venta", "quedó anulada por completo");
      break;

    case "socio.update": {
      // Qué cambió, campo a campo: "Saldo: 120 € → 95 €".
      const changes = details.changes || {};
      const censurados = [];
      for (const [field, change] of Object.entries(changes)) {
        if (!change || typeof change !== "object") continue;
        // Si el backend mandara el valor de un campo sensible (regresión), NO se pinta:
        // se degrada al mismo trato que los redactados.
        if (NEVER_SHOW_VALUE.has(field)) {
          censurados.push(field);
          continue;
        }
        push(clientField(field), `${fmtChangeValue(change.from)} → ${fmtChangeValue(change.to)}`);
      }
      // Campos censurados por el backend (identidad, contacto, salud, contraseña): consta
      // que se tocaron, nunca su contenido. No hay valor que pintar, solo el hecho.
      const redacted = [...new Set([...(details.redacted_fields || []), ...censurados])];
      if (redacted.length) {
        push("También se actualizó", redacted.map(clientField).join(", "));
      }
      break;
    }

    case "socio.create":
    case "staff.create":
    case "owner.create":
      push("Email", details.email);
      break;

    // Asistencia: la fila ya lo dice todo en el resumen («Entrada de Ana Rosa
    // Clienta»). Sin este `case` caía al `default`, que vuelca las claves planas y
    // dejaba a la vista el `entry_id: b203a8e3-0af4-…` —el UUID crudo de la fila de
    // `entries`, que ninguna pantalla usa: no hay drill-down por entrada— y un
    // `closed: 1` que no significa nada. Los dos siguen en la BD para auditoría; el
    // `email` también, porque el buscador del servidor mira dentro de `details`.
    case "socio.entrada":
      break;

    case "socio.salida":
      // La excepción: cerrar MÁS DE UNA entrada de golpe es la huella de un doble
      // check-in, y eso sí merece verse. Con una sola, callar.
      if (Number(details.closed) > 1) push("Entradas cerradas", details.closed);
      break;

    default: {
      // Acción nueva en el backend y todavía sin receta aquí: se listan las claves planas
      // tal cual, para degradar a algo legible en vez de volver al JSON.
      //
      // Pero a ciegas no: cualquier acción futura que registre un campo sensible acabaría
      // pintándolo aquí sin que nadie lo revisara. Se listan por nombre, sin valor.
      const censurados = [];
      for (const [key, value] of Object.entries(details)) {
        if (value === null || value === undefined || value === "") continue;
        if (typeof value === "object") continue;
        // La censura va PRIMERO: `national_id` acaba en `_id` y el filtro de abajo
        // se lo tragaba en silencio, cuando de un campo sensible tiene que constar
        // que se tocó (lo cazó su propio test, 08-09-2026).
        if (NEVER_SHOW_VALUE.has(key)) {
          censurados.push(key);
          continue;
        }
        // Los UUID no son para el staff: no hay pantalla que busque por ellos y
        // convierten la fila en una morcilla («transaction_id: 94e30f1d-fc7f-…»).
        // Siguen en la BD para auditoría, que es donde sirven.
        if (/_id$/.test(key) || key === "id") continue;
        push(key, value);
      }
      if (censurados.length) {
        push("También se registró", censurados.map(clientField).join(", "));
      }
      break;
    }
  }

  return out;
}

export default formatActivityDetails;
